import React from 'react'
import { produce } from 'immer'
import { initializeWithCountIfNotInitialized } from '@mobi/account/Areas/Deposit/Utils'
import { useDispatch } from 'react-redux'
import { closeModal } from '@core/Components/Modal'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { OverlayClose } from '@core/Components/Overlay'
import { navigateToChangeTempPassword } from '@core/Areas/ChangePassword/ChangeTemporaryPassword'
import { navChange } from '@classic/Foundation/Navigation/Nav'
import {
  recordLastSuccessfulLogin,
  setIsBiometricsEnabledOnTempPasswordChange,
  setIsKeepMeLoggedIn,
  setIsLoginShown,
  setTrustedDeviceDetails,
} from '../Store'
import { getIsKeepMeLoggedIn, getLoginActions, getTrustedDeviceDetails } from '../Store/selectors'
import { attemptLogin, getRememberAccountNumber, refreshLogonState } from '../helpers'
import { trackLoginSuccess } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { logUserId } from '@core/Services/Sentry'
import { getFromLocalStorage, setInLocalStorage } from '@mobi/utils/storage'
import { sendToNative } from '@mobi/web-native-comms/web'
import { completeLogonWithMfa } from '../helpers/api'
import { LogonRequestData, LogonResult, MFAServerMethod } from '@mobi/api-types'
import { incrementQuickCounter } from '@core/Services/Analytics/helpers/quickCounter'
import { useAppSelector } from '@core/Store/hooks'
import { v4 as uuidv4 } from 'uuid'

const REMEMBER_ACCOUNT_STORAGE_KEY = 'remember_account_number'
const MAX_UNSUCCESSFUL_MFA_ATTEMPTS = 3

const DEFAULT_LOGIN_OPTIONS: LoginOptions = {
  userHasToggledBiometrics: false,
  isBiometricsToggleOn: false,
  withMfa: false,
}

export const useLoginState = (onLoginSuccess?: VoidFunction): UseLoginStateReturn => {
  const dispatch = useDispatch()
  const { onLoginSuccessCallback, onLoginSuccessRedirectUrl, onLoginCancelledRedirectUrl } =
    useAppSelector(getLoginActions)

  const isKeepMeLoggedIn = useAppSelector(getIsKeepMeLoggedIn)
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)
  const newDeviceSecret = uuidv4()

  const [workflow, setWorkflow] = React.useState<Workflow>('Login')

  React.useEffect(() => {
    if (
      workflow === 'Password Reset' ||
      workflow === 'Account Number Recovery' ||
      workflow === 'Login'
    ) {
      setPassword('')
      setErrorMessage(undefined)
    }
  }, [workflow])

  const [accountIdentifier, setAccountIdentifier] = React.useState('')
  const [validatedAccountNumber, setValidatedAccountNumber] = React.useState<number>()
  const [password, setPassword] = React.useState('')
  const [rememberAccount, setRememberAccount] = React.useState(getRememberAccount())
  const [mfaVerificationCode, setMfaVerificationCode] = React.useState('')
  const [mfaSession, setMfaSession] = React.useState<MfaSession>()

  const [errorMessage, setErrorMessage] = React.useState<string>()
  const [isBusy, setIsBusy] = React.useState(false)

  React.useEffect(() => {
    getRememberAccountNumber().then(accountNumber => {
      if (!accountNumber) return
      setAccountIdentifier(accountNumber.toString())
      logUserId(accountNumber)
      initializeWithCountIfNotInitialized(accountNumber, 2)
    })
  }, [setAccountIdentifier])

  const resetErrorMessage = () => setErrorMessage(undefined)
  const toggleKeepLoggedIn = () => dispatch(setIsKeepMeLoggedIn(!isKeepMeLoggedIn))
  const toggleRememberAccount = () => setRememberAccount(val => !val)

  const handleCloseLogin = (shouldRedirectOnClose = true) => {
    if (shouldRedirectOnClose && onLoginCancelledRedirectUrl) navChange(onLoginCancelledRedirectUrl)
    dispatch(setIsLoginShown({ shouldShow: false }))
  }

  const performLoginSuccessActions = React.useCallback(
    (
      accountNumber: number,
      {
        userHasToggledBiometrics,
        isBiometricsToggleOn,
        withMfa,
      }: LoginOptions = DEFAULT_LOGIN_OPTIONS
    ) => {
      saveRememberAccount(rememberAccount)

      if (userHasToggledBiometrics) {
        sendToNative('REQUEST_TOGGLE_BIOMETRICS', {
          accountNumber,
          password: password,
          doNotReauthenticate: true,
        })

        incrementQuickCounter(`login-biometrics-changed-to-${isBiometricsToggleOn}`)
      }

      if (onLoginSuccessRedirectUrl) {
        navChange(onLoginSuccessRedirectUrl)
      }

      dispatch(
        recordLastSuccessfulLogin({
          accountIdentifier,
          loginDateTime: new Date().toISOString(),
          withMfa,
          withBiometrics: isBiometricsToggleOn,
        })
      )

      onLoginSuccessCallback?.()

      dispatch(setIsLoginShown({ shouldShow: false }))

      incrementQuickCounter('login-successful')
    },
    [password, rememberAccount, onLoginSuccessRedirectUrl, onLoginSuccessCallback, dispatch]
  )

  const handleLogin = async (options?: LoginOptions) => {
    if (workflow === 'Login') {
      performLogin(options)
    }
  }

  const performLogin = async (options: LoginOptions = DEFAULT_LOGIN_OPTIONS) => {
    const { userHasToggledBiometrics } = options

    setIsBusy(true)

    const storedDeviceDetailsForAccount = trustedDevices[accountIdentifier]

    const result = await attemptLogin({
      accountIdentifier,
      password,
      rememberAccount,
      keepLoggedIn: isKeepMeLoggedIn,
      deviceSecret: storedDeviceDetailsForAccount?.deviceSecret ?? newDeviceSecret,
      deviceGroupKey: storedDeviceDetailsForAccount?.deviceGroupKey,
      deviceKey: storedDeviceDetailsForAccount?.deviceKey,
    })

    setIsBusy(false)

    switch (result.status) {
      case 'SUCCESSFUL': {
        setValidatedAccountNumber(result.accountNumber)
        trackLoginSuccess(result.accountNumber.toString(), isKeepMeLoggedIn, rememberAccount, false)
        incrementQuickCounter('login-successful-after-password')
        // For existing users we want to initialize the successful count to be at least 2
        // See https://rwwa.atlassian.net/browse/MOBI-2467
        initializeWithCountIfNotInitialized(result.accountNumber, 2)
        if (result.newDeviceKey && result.newDeviceGroupKey) {
          dispatch(
            setTrustedDeviceDetails({
              accountIdentifier,
              deviceDetails: {
                deviceGroupKey: result.newDeviceGroupKey,
                deviceKey: result.newDeviceKey,
                deviceSecret: newDeviceSecret,
                isRemembered: false,
              },
            })
          )
        }
        performLoginSuccessActions(result.accountNumber, {
          userHasToggledBiometrics: options.userHasToggledBiometrics,
          isBiometricsToggleOn: options.isBiometricsToggleOn ?? false,
          withMfa: false,
        })
        break
      }
      case 'MFA_REQUIRED': {
        handleStartMfaVerification(result, options)
        break
      }
      case 'PASSWORD_CHANGE_REQUIRED': {
        if (userHasToggledBiometrics) {
          dispatch(setIsBiometricsEnabledOnTempPasswordChange(true))
        }
        navigateToChangeTempPassword({
          accountNumber: result.accountNumber.toString(),
          password,
        })
        break
      }
      default: {
        if (result.errorMessage) setErrorMessage(result.errorMessage)
        break
      }
    }
  }

  const performMfa = async (codeOverride?: string) => {
    if (!mfaSession || !validatedAccountNumber) {
      return
    }

    setIsBusy(true)

    const { id, factor, attempts } = mfaSession
    const newAttempts = attempts + 1

    setMfaSession(
      produce(draft => {
        if (draft) draft.attempts = newAttempts
      })
    )

    const result = await completeLogonWithMfa({
      accountNumber: validatedAccountNumber,
      mfaSessionId: id,
      mfaFactor: factor,
      mfaCode: codeOverride ?? mfaVerificationCode,
      rememberAccount,
      keepLoggedIn: isKeepMeLoggedIn,
    })

    refreshLogonState()

    setIsBusy(false)

    if (!result.isSuccessful) {
      if (newAttempts >= MAX_UNSUCCESSFUL_MFA_ATTEMPTS) {
        setMfaSession(undefined)
        setMfaVerificationCode('')
        setErrorMessage('Too many unsuccessful attempts')
      } else {
        setErrorMessage('Sorry the code you have provided is incorrect')
      }
      return
    }

    if (result.newDeviceKey && result.newDeviceGroupKey) {
      dispatch(
        setTrustedDeviceDetails({
          accountIdentifier,
          deviceDetails: {
            deviceGroupKey: result.newDeviceGroupKey,
            deviceKey: result.newDeviceKey,
            deviceSecret: newDeviceSecret,
            isRemembered: false,
          },
        })
      )
    }

    trackLoginSuccess(validatedAccountNumber.toString(), isKeepMeLoggedIn, rememberAccount, false)
    incrementQuickCounter('login-successful-after-mfa')

    // For existing users we want to initialize the successful count to be at least 2
    // See https://rwwa.atlassian.net/browse/MOBI-2467
    initializeWithCountIfNotInitialized(validatedAccountNumber, 2)
    performLoginSuccessActions(validatedAccountNumber, {
      userHasToggledBiometrics: mfaSession.loginOptions?.userHasToggledBiometrics ?? false,
      isBiometricsToggleOn: mfaSession.loginOptions?.isBiometricsToggleOn ?? false,
      withMfa: true,
    })
    onLoginSuccess?.()
  }

  const handleSignUp = () => {
    // Close Quickbet & BetSlip if open
    dispatch(closeModal({ id: QUICKBET_MODAL_ID })) && OverlayClose()
    OverlayClose()
    navChange('/SignUp')
    handleCloseLogin(false)
  }

  const handleStartMfaVerification = (result: LogonResult, options?: LoginOptions) => {
    if (result.status === 'MFA_REQUIRED') {
      const { mfaSessionId, mfaFactor, mfaHint, accountNumber } = result
      setValidatedAccountNumber(accountNumber)
      setMfaSession({
        id: mfaSessionId,
        factor: mfaFactor,
        hint: mfaHint,
        attempts: 0,
        loginOptions: options,
      })
      setWorkflow('Login Verification')
    }
  }

  const handleMfaChallenge = async (code: string) => {
    if (mfaSession) {
      await performMfa(code)
    }
    setMfaVerificationCode(code)
  }

  const handleMfaChallengeResend = async () => {
    const options = mfaSession?.loginOptions
    setMfaSession(undefined)
    setMfaVerificationCode('')
    await performLogin(options)
  }

  const onAccountIdentifierChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setAccountIdentifier(e.target.value)
    resetErrorMessage()
  }

  const onPasswordChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setPassword(e.target.value)
    resetErrorMessage()
  }

  const onMfaVerificationCodeChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setMfaVerificationCode(e.target.value)
    resetErrorMessage()
  }

  const onLoginDataChange = (loginData: LogonRequestData) => {
    setAccountIdentifier(loginData.accountIdentifier)
    setPassword(loginData.password)
    setRememberAccount(loginData.rememberAccount)
    setIsKeepMeLoggedIn(loginData.keepLoggedIn)
  }

  return {
    state: {
      accountIdentifier,
      accountNumber: validatedAccountNumber,
      password,
      rememberAccount,
      keepLoggedIn: isKeepMeLoggedIn,
      mfaVerificationCode,
      mfaFactor: mfaSession?.factor,
      mfaHint: mfaSession?.hint,
      isBusy,
      errorMessage,
      workflow,
    },
    handlers: {
      login: (options?: LoginOptions) => handleLogin(options),
      signUp: handleSignUp,
      close: handleCloseLogin,
      onAccountIdentifierChange,
      onPasswordChange,
      onMfaVerificationCodeChange,
      onLoginDataChange,
      handleStartMfaVerification,
      handleMfaChallenge,
      handleMfaChallengeResend,
      performLoginSuccessActions,
      toggleKeepLoggedIn,
      toggleRememberAccount,
      setWorkflow,
      setErrorMessage,
    },
  }
}

export function getRememberAccount(): boolean {
  const defaultValue = true
  const store = getFromLocalStorage(REMEMBER_ACCOUNT_STORAGE_KEY)
  if (!store) return defaultValue

  return JSON.parse(store) ?? defaultValue
}

// =============
// Local Helpers
// =============

function saveRememberAccount(shouldRememberAccount: boolean): void {
  setInLocalStorage(REMEMBER_ACCOUNT_STORAGE_KEY, shouldRememberAccount.toString())
}

// =====
// Types
// =====
interface UseLoginStateReturn {
  state: {
    accountIdentifier: string
    accountNumber?: number
    password: string
    rememberAccount: boolean
    keepLoggedIn: boolean
    mfaVerificationCode: string
    mfaFactor?: MFAServerMethod
    mfaHint?: string
    isBusy: boolean
    workflow: Workflow
    errorMessage?: string
  }
  handlers: {
    login(options?: LoginOptions): Promise<void>
    signUp(): void
    close(): void
    onAccountIdentifierChange: React.ChangeEventHandler<HTMLInputElement>
    onPasswordChange: React.ChangeEventHandler<HTMLInputElement>
    onMfaVerificationCodeChange: React.ChangeEventHandler<HTMLInputElement>
    onLoginDataChange: (loginData: LogonRequestData) => void
    handleStartMfaVerification?: (result: LogonResult, options?: LoginOptions) => void
    handleMfaChallenge(code: string): Promise<void>
    handleMfaChallengeResend(): Promise<void>
    performLoginSuccessActions(accountNumber: number): void
    toggleKeepLoggedIn(): void
    toggleRememberAccount(): void
    setWorkflow: React.Dispatch<React.SetStateAction<Workflow>>
    setErrorMessage: React.Dispatch<React.SetStateAction<string | undefined>>
  }
}

type LoginOptions = {
  userHasToggledBiometrics: boolean
  isBiometricsToggleOn: boolean
  withMfa: boolean
}

type Workflow = 'Login' | 'Login Verification' | 'Password Reset' | 'Account Number Recovery'

type MfaSession = {
  id: string
  factor: 'SMS' | 'EMAIL'
  hint: string
  attempts: number
  loginOptions?: LoginOptions
}
